import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { of, Subject, Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { Pack, PackFactory, Component, ComponentFactory } from "@edp-components/edp-pack/edp-pack.model";
import { Discount, DiscountFactory, IDiscount } from "@edp-components/edp-pack-discount/edp-pack-discount.model";
import { IDependents } from "@edp-models/edp-pack-subscriber.model";
import { ApiService } from "./api.service";
import { EnvService } from "./env.service";
import { Router } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
    providedIn: "root",
})
export class PackService {
    public discountChanged$: Subject<Discount> = new Subject<Discount>();
    public contacertaChanged$: Subject<number> = new Subject<number>();
    public dependentsChanged$: Subject<IDependents[]> = new Subject<IDependents[]>();
    public _discountsLoaded$: Subject<boolean> = new Subject<boolean>();
    private cache: Pack[];
    private discount: Discount;
    private totalPackValue: string;

    constructor(
        private http: HttpClient,
        private apiService: ApiService,
        private envService: EnvService,
        private router: Router,
        private toast: EdpToasterService,
        private translate: TranslateService,
    ) { }

    public getAll(body: object, fromCache: boolean = true): Observable<Pack[]> {
        if (fromCache && this.cache) {
            return of(this.cache);
        }

        return this.apiService.callServicePostToCollection<Pack[]>("pack.getAll", body)
            .pipe(
                map(packs => {
                    if (!(packs instanceof Error)) {
                        this.cache = packs.map(pack => this.createPack(pack));
                    }

                    return this.cache;
                })
            );
    }

    public getDiscounts(packId: string, contractId: string): Observable<Discount[]> {
        const body = {
            rule: "packs",
            pack_id: packId,
            contract_id: contractId
        };

        return this.apiService.callServicePostToCollection<Discount[]>("pack.getDiscounts", body)
            .pipe(
                map(discounts => {
                    if (!(discounts instanceof Error)) {
                        return discounts.map(discount => this.createDiscount(discount));
                    }

                    // Discart errors
                    // return discounts;
                })
            );
    }

    public checkContracts(body: object, fromCache: boolean = true): Observable<any[]> {
        return this.apiService.callServicePostToCollection<any>("pack.checkContracts", body)
            .pipe(
                map(data => {
                    if (!(data instanceof Error)) {
                        sessionStorage.setItem("service-prices", data.service_prices);
                        return data;
                    }

                    // Discart errors
                    // return data;
                })
            );
    }

    public createPack(pack: any): Pack {

        const brandImage = !!pack.brand_resource_id ? `${this.envService.appUrl()}/resource/image/${pack.brand_resource_id}` : null;
        let packType = null;
        let packName = null;

        if (pack.isEasy || pack.type === 1) {
            packType = 1;
            packName = "pt.edp.edponline.pack-component.packeasy";
        }
        if (pack.isSmart || pack.type === 2) {
            packType = 2;
            packName = "pt.edp.edponline.pack-component.packsmart";
        }
        if (pack.isFull || pack.type === 3 ) {
            packType = 3;
            packName = "pt.edp.edponline.pack-component.packfull";
        }

        return PackFactory.create({
            id: pack.id,
            type: packType,
            name: packName,
            title: pack.title,
            color: pack.color,
            brandImage,
            description: pack.description,
            discount: pack.discount ? pack.discount : 0,
            value: pack.value,
            unit: pack.unit,
            id_crm: pack.id_crm,
            components: this.createComponentCollection(pack.components),
            is_available: pack.is_available,
            is_contracted: pack.is_contracted,
            contracts: pack.contracts,
            recomend: pack.recomend,
            save: pack.save
        });
    }

    public setDiscount(discount: Discount) {
        this.discountChanged$.next(discount);
        this.discount = discount;
    }

    public getDiscount(): Discount {
        return this.discount;
    }

    public setTotalPackValue(finalValue: string) {
        this.totalPackValue = finalValue;
    }

    public getTotalPackValue(): string {
        return this.totalPackValue;
    }

    private createComponentCollection(components: any[]): Component[] {
        return components.map(component => {
            let value = null;
            let unit = null;
            switch (component.type) {
                case "text":
                    value = `${this.envService.appUrl()}/resource/image/${component.value}`;
                    break;
                case "numeric":
                default:
                    value = component.value;
                    unit = component.unit;
                    break;
            }

            return ComponentFactory.create({
                id: component.id,
                title: component.title,
                description: component.description,
                description_mobile: component.description_mobile,
                link: component.link,
                value,
                unit,
                type: component.type,
                status: component.status,
                category: {
                    id: component.category.id,
                    icon: component.category.icon,
                    label: component.category.label,
                    title: component.category.title,
                    reference: component.category.reference,

                },
            });
        });
    }

    private createDiscount(discount: IDiscount): Discount {
        return DiscountFactory.create({
            id: discount.id,
            title: discount.title,
            description: discount.description,
            values: discount.values,
            tariff: discount.tariff,
            contractType: discount.contractType,
            isChecked: discount.isChecked,
            form: discount.form,
            current: discount.current,
            type: discount.type,
        });
    }

    public getSimulator(body: { contract_id: number | string; pack?: string | null }) {
        return this.apiService.callServicePostToCollection(`pack.getSimulationPack`, body).pipe(
            map(data => {
                return data;
            }),
            catchError(error => {
                return of(null);
            })
        );
    }

    private showToastPackUnavailable(): void {
        const label = this.translate.instant("pt.edp.edponline.new.pack.unavailable");
        this.toast.create(label, EdpToastType.info, { timeout: 3000 }).show();
    }

    public goToNewPlans(contract: Contract, routerPath?: string): void {
        // PLAT-60649 - no available to contract with indexed offer
        if (!contract?.isIndexedOffer()) {
            const path = routerPath || "/novo-plano";
            this.router.navigate([path]);
            return;
        }
        this.showToastPackUnavailable();
    }
}
